import { useEffect } from "react";

class WebSocketManager {
  constructor() {
    this.ws = null;
    this.url = this.getWebSocketURL();
    this.listeners = new Set();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 50;
    this.reconnectDelay = 3000;
    this.keepAliveTimer = null;
    this.isConnecting = false;
    this.manualDisconnect = false;
    this.reconnectTimer = null;
    this.suspendedByVisibility = false;
    this.visibilityHandler = this.handleVisibilityChange.bind(this);

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.visibilityHandler);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("pagehide", this.visibilityHandler);
      window.addEventListener("pageshow", this.visibilityHandler);
    }
  }

  normalizeWsUrl(rawUrl, defaultPath = "/ws") {
    const cleanUrl = String(rawUrl || "")
      .trim()
      .replace(/^['"]|['"]$/g, "");

    if (!cleanUrl) return "";

    try {
      const parsed = new URL(cleanUrl, window.location.origin);

      if (parsed.protocol === "http:") parsed.protocol = "ws:";
      if (parsed.protocol === "https:") parsed.protocol = "wss:";

      let path = String(parsed.pathname || "")
        .replace(/\/{2,}/g, "/")
        .replace(/\/+$/g, "");

      if (!path || path === "/") path = defaultPath;
      if (!path.startsWith("/")) path = `/${path}`;

      parsed.pathname = path;
      return parsed.toString();
    } catch {
      // Fallback seguro para URLs no parseables.
      const [base, ...rest] = cleanUrl.split("://");
      if (rest.length === 0) return cleanUrl;
      const normalized = rest.join("://").replace(/\/{2,}/g, "/");
      return `${base}://${normalized}`;
    }
  }

  /**
   * Obtener URL del WebSocket (soporta desarrollo y producción)
   */
  getWebSocketURL() {
    const cleanEnv = (value) =>
      String(value || "")
        .trim()
        .replace(/^['"]|['"]$/g, "");

    const wsUrl = this.normalizeWsUrl(cleanEnv(process.env.REACT_APP_WS_URL), "/ws");
    if (wsUrl) return this.attachToken(wsUrl);

    // Fallback preferido: derivar desde API_URL para respetar prefijos (ej: /api).
    const apiUrl = cleanEnv(process.env.REACT_APP_API_URL);
    if (apiUrl) {
      try {
        const parsed = new URL(apiUrl, window.location.origin);
        const wsProtocol = parsed.protocol === "https:" ? "wss:" : "ws:";
        let path = parsed.pathname.replace(/\/+$/g, "");
        path = path ? `${path}/ws` : "/ws";
        return this.attachToken(this.normalizeWsUrl(`${wsProtocol}//${parsed.host}${path}`, "/ws"));
      } catch (error) {
        console.warn("⚠️ REACT_APP_API_URL inválida para WS, usando fallback por host:", error);
      }
    }

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const host = window.location.host;
    return this.attachToken(this.normalizeWsUrl(`${protocol}//${host}/ws`, "/ws"));
  }

  attachToken(rawUrl) {
    try {
      const token = localStorage.getItem("token");
      if (!token) return rawUrl;
      const parsed = new URL(rawUrl, window.location.origin);
      parsed.searchParams.set("token", token);
      return parsed.toString();
    } catch {
      return rawUrl;
    }
  }

  /**
   * Conectar al WebSocket
   */
  connect() {
    if (this.ws && [WebSocket.OPEN, WebSocket.CONNECTING].includes(this.ws.readyState)) {
      console.warn("⚠️ WebSocket ya está conectado o conectando");
      return false;
    }

    this.manualDisconnect = false;
    this.isConnecting = true;
    this.suspendedByVisibility = false;
    this.url = this.getWebSocketURL();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    try {
      console.log(`🔌 Conectando a: ${this.url}`);
      this.ws = new WebSocket(this.url);

      this.ws.onopen = () => {
        console.log('✅ WebSocket conectado');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.startKeepAlive();
        this.notifyListeners({
          type: 'connection',
          status: 'connected',
        });
      };

      this.ws.onmessage = (event) => {
        try {
          const rawPayload = event?.data;
          let data = rawPayload;

          // WS puede entregar texto JSON o payload ya serializado.
          if (typeof rawPayload === "string") {
            try {
              data = JSON.parse(rawPayload);
            } catch {
              data = {
                type: "log",
                level: "INFO",
                message: rawPayload,
                timestamp: new Date().toISOString(),
              };
            }
          }

          // Fallback: si llega texto plano, lo publicamos como log.
          if (!data || typeof data !== "object") {
            data = {
              type: "log",
              level: "INFO",
              message: String(rawPayload || ""),
              timestamp: new Date().toISOString(),
            };
          }

          this.notifyListeners(data);
          this.handleSpecificEvent(data);
        } catch (error) {
          console.error('❌ Error procesando mensaje:', error);
        }
      };

      this.ws.onerror = () => {
        console.error(`❌ Error WebSocket en ${this.url} (handshake/red/proxy)`);
        this.isConnecting = false;
        this.notifyListeners({
          type: "connection",
          status: "error",
          url: this.url,
        });
      };

      this.ws.onclose = (event) => {
        const code = event?.code ?? 1006;
        const reason = event?.reason || "sin razón";
        const wasClean = Boolean(event?.wasClean);

        console.warn(`❌ WebSocket cerrado (code=${code}, clean=${wasClean}, reason=${reason})`);
        this.isConnecting = false;
        this.stopKeepAlive();
        this.ws = null;
        this.notifyListeners({
          type: "connection",
          status: "disconnected",
          code,
          reason,
          clean: wasClean,
        });
        if (!this.manualDisconnect) this.reconnect();
      };
    } catch (error) {
      console.error('❌ Error conectando:', error);
      this.isConnecting = false;
      if (!this.manualDisconnect) this.reconnect();
    }

    return true;
  }

  handleVisibilityChange() {
    if (typeof document === "undefined") return;
    if (document.hidden) {
      this.suspendedByVisibility = true;
      this.disconnect({ manual: true, reason: "visibility_hidden" });
      return;
    }
    if (this.suspendedByVisibility) {
      this.suspendedByVisibility = false;
      this.connect();
    }
  }

  /**
   * Manejar eventos específicos
   */
  handleSpecificEvent(data) {
    if (!data.type) return;

    switch (data.type) {
      case 'compra':
      case 'buy_event':
        this.handleCompraEvent(data);
        break;
      case 'venta':
      case 'sell_event':
        this.handleVentaEvent(data);
        break;
      case 'order_update':
        this.handleOrderUpdate(data);
        break;
      default:
        break;
    }
  }

  /**
   * Manejar evento de compra
   */
  handleCompraEvent(data) {
    const method = data?.entry_method || data?.method || "";
    const sl = data?.sl;
    const slPct = data?.sl_pct;
    const tp = data?.tp;
    const tpPct = data?.tp_pct;
    const hz = data?.hz_horas;
    let msg = `${data.symbol} - ${data.size} unidades @ $${data.price}`;
    if (method) msg += ` [${method}]`;
    if (sl != null) {
      msg += ` | SL: $${sl}`;
      if (slPct != null) msg += ` (${Number(slPct).toFixed(2)}%)`;
    }
    if (tp != null) {
      msg += ` | TP: $${tp}`;
      if (tpPct != null) msg += ` (${Number(tpPct).toFixed(2)}%)`;
    }
    if (hz != null) msg += ` | HZ: ${hz}h`;
    this.playSound('compra');
    this.showNotification({
      type: 'compra',
      title: '✅ ¡COMPRA!',
      message: msg,
      icon: '🟢',
      color: '#00ff88',
    });
  }

  /**
   * Manejar evento de venta
   */
  handleVentaEvent(data) {
    const rawReason = data?.razon_venta || data?.reason || data?.exit_type || "";
    const reasonText = rawReason ? ` | ${String(rawReason).toUpperCase()}` : "";
    const entryPrice = data?.entry_price;
    const slPrice = data?.sl;
    const tpPrice = data?.tp;
    const slPct = data?.sl_pct;
    const tpPct = data?.tp_pct;
    const pnl = data?.pnl;
    const pnlPct = data?.pnl_percentage;
    let details = `Salida: $${data.price}`;
    if (entryPrice != null) details += ` | Entrada: $${entryPrice}`;
    if (slPrice != null) {
      details += ` | SL: $${slPrice}`;
      if (slPct != null) details += ` (${Number(slPct).toFixed(2)}%)`;
    }
    if (tpPrice != null) {
      details += ` | TP: $${tpPrice}`;
      if (tpPct != null) details += ` (${Number(tpPct).toFixed(2)}%)`;
    }
    if (pnl != null) details += ` | P&L: $${Number(pnl).toFixed(2)} (${Number(pnlPct).toFixed(2)}%)`;
    this.playSound('venta');
    this.showNotification({
      type: 'venta',
      title: '💰 ¡VENTA!',
      message: `${data.symbol} - ${data.size} unidades${reasonText}\n${details}`,
      icon: '🔴',
      color: '#ff3232',
    });
  }

  /**
   * Manejar actualización de orden
   */
  handleOrderUpdate(data) {
    this.notifyListeners({
      type: 'order_update',
      data,
    });
  }

  /**
   * Reproducir sonido
   */
  playSound(type) {
    try {
      const soundMap = {
        compra: '/sonido_compra_monedas.mp3',
        venta: '/sonido_venta_campana.mp3',
      };

      const audioFile = soundMap[type];
      if (!audioFile) return;

      const audio = new Audio(audioFile);
      audio.volume = 0.7;
      audio.preload = "none";
      audio.play().catch((e) => console.warn(`⚠️ No se reprodujo ${type}`));
      audio.onended = () => {
        audio.src = "";
      };
    } catch (error) {
      console.error('❌ Error en sonido:', error);
    }
  }

  /**
   * Mostrar notificación en el navegador
   */
  showNotification(notification) {
    try {
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification(notification.title, {
          body: notification.message,
          icon: '/ayc.png',
          tag: notification.type,
          requireInteraction: true,
        });
      }

      this.notifyListeners({
        type: 'notification',
        notification,
      });
    } catch (error) {
      console.error('❌ Error notificación:', error);
    }
  }

  /**
   * Solicitar permiso para notificaciones
   */
  requestNotificationPermission() {
    try {
      if (!('Notification' in window)) {
        console.warn('⚠️ Notificaciones no soportadas');
        return;
      }

      if (Notification.permission === 'granted') {
        console.log('✅ Notificaciones ya permitidas');
        return;
      }

      if (Notification.permission === 'default') {
        Notification.requestPermission().then((permission) => {
          console.log(`✅ Permiso: ${permission}`);
        });
      }
    } catch (error) {
      console.error('❌ Error permiso:', error);
    }
  }

  /**
   * Intentar reconectar
   */
  reconnect() {
    if (this.manualDisconnect) return;

    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Máx. intentos reconexión alcanzado');
      this.notifyListeners({
        type: "connection",
        status: "failed",
        attempts: this.reconnectAttempts,
      });
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    console.log(`🔄 Reintentando en ${delay}ms...`);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  /**
   * Suscribirse a eventos
   */
  subscribe(callback) {
    if (typeof callback !== 'function') {
      console.error('❌ Callback debe ser función');
      return () => {};
    }
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notificar a todos los listeners
   */
  notifyListeners(data) {
    this.listeners.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error('❌ Error listener:', error);
      }
    });
  }

  /**
   * Enviar mensaje al servidor
   */
  send(data) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('⚠️ WebSocket no conectado');
      return false;
    }

    let payload = data;

    // Evita enviar accidentalmente un Event del DOM/React (ej: onClick directo).
    if (payload && typeof payload === "object" && "isTrusted" in payload) {
      payload.preventDefault?.();
      payload.stopPropagation?.();

      const candidate =
        payload?.detail ??
        payload?.target?.value ??
        payload?.currentTarget?.value;

      if (candidate === undefined) {
        console.error("❌ wsManager.send recibió un Event en lugar de un payload válido");
        return false;
      }

      payload = candidate;
    }

    if (typeof payload === "string") {
      this.ws.send(payload);
      return true;
    }

    this.ws.send(JSON.stringify(payload));
    return true;
  }

  /**
   * Desconectar
   */
  disconnect(options = {}) {
    const { manual = true } = options;
    this.manualDisconnect = manual;
    this.isConnecting = false;
    this.stopKeepAlive();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close(1000, "client_disconnect");
      } catch {}
      this.ws = null;
    }
  }

  /**
   * Mantener viva la conexión enviando un ping periódico.
   * Evita que la conexión idle se cierre (especialmente con bots detenidos
   * cuyos logs no crecen y no generan tráfico).
   */
  startKeepAlive() {
    this.stopKeepAlive();
    this.keepAliveTimer = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      try {
        this.ws.send(JSON.stringify({ type: "ping", ts: Date.now() }));
      } catch {}
    }, 25000);
  }

  stopKeepAlive() {
    if (this.keepAliveTimer) {
      clearInterval(this.keepAliveTimer);
      this.keepAliveTimer = null;
    }
  }

  /**
   * Obtener estado de conexión
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Obtener estado legible
   */
  getStatus() {
    if (!this.ws) return 'DISCONNECTED';
    const states = { 0: 'CONNECTING', 1: 'CONNECTED', 2: 'CLOSING', 3: 'CLOSED' };
    return states[this.ws.readyState] || 'UNKNOWN';
  }
}

// ✅ INSTANCIA SINGLETON GLOBAL
const wsManager = new WebSocketManager();

// ✅ HOOK PARA USAR EN COMPONENTES
const useWebSocketData = () => {
  useEffect(() => {
    console.log('🚀 Inicializando WebSocket...');
    wsManager.requestNotificationPermission();
    wsManager.connect();

    return () => {
      console.log('🛑 Limpiando WebSocket...');
      wsManager.disconnect();
    };
  }, []);

  return wsManager;
};

// ✅ COMPONENTE PARA MONTARSE EN RAÍZ
const WebSocketData = () => {
  useWebSocketData();
  return null;
};

// ✅ EXPORTAR TODO CLARAMENTE
export default WebSocketData;
export { useWebSocketData, wsManager, WebSocketManager };
